import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RenewalHelpCard from '../components/RenewalHelpCard';
import {useRenewalClick} from '../context/RenewalClickContext';
import {fireRenewalJourneyActivity} from '../api/renewalClickApi';
import {PAGE_STATES, RenewalJourneyEvent, RenewalJourneyLocation} from '../constants/renewalClick';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('he-IL', {style: 'currency', currency: 'ILS', maximumFractionDigits: 0}).format(amount);

const styles = {
  container: {px: 2, pb: 5, maxWidth: 560, mx: 'auto', textAlign: 'center'},
  iconWrapper: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    bgcolor: 'success.light',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
    mt: 4,
    mb: 2.5,
  },
  title: (theme) => ({
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: 'secondary.main',
    mb: 1,
  }),
  subtitle: (theme) => ({
    fontSize: theme.typography.pxToRem(14),
    color: 'text.secondary',
    mb: 3,
    lineHeight: 1.6,
  }),
  card: {
    border: 1,
    borderColor: 'divider',
    borderRadius: '12px',
    p: 2.5,
    mb: 3,
    textAlign: 'right',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 1.25,
  },
  rowLabel: (theme) => ({fontSize: theme.typography.pxToRem(13), color: 'text.secondary'}),
  rowValue: (theme) => ({fontSize: theme.typography.pxToRem(14), fontWeight: 600, color: 'secondary.main'}),
  priceValue: (theme) => ({
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
  }),
    emailValue: (theme) => ({
      fontSize: theme.typography.pxToRem(12),
      fontWeight: theme.typography.fontWeightBold,
      color: 'secondary.main',
    }),
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    mt: 1.5,
  },
  statusText: (theme) => ({fontSize: theme.typography.pxToRem(13), color: 'text.secondary'}),
  nextSection: {
    borderRadius: '12px',
    p: 2.5,
    mb: 3,
    textAlign: 'right',
  },
  nextTitle: (theme) => ({
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    color: 'secondary.main',
    mb: 1.5,
    textAlign: 'left',
  }),
  nextItem: {
    display: 'flex',
    alignItems: 'flex-start',
    textAlign: 'left',
    gap: 1,
    mb: 1.25,
  },
  nextItemText: (theme) => ({fontSize: theme.typography.pxToRem(13), color: 'text.secondary', lineHeight: 1.5}),
};

const CompletionScreen = () => {
  const intl = useIntl();
  const m = (id, values) => intl.formatMessage({id}, values);
  const {vehicle, pricing, opportunityId, pageState} = useRenewalClick();
  const hasTracked = useRef(false);

  useEffect(() => {
    // The renewal was already submitted on a previous visit — don't refire it here.
    if (!hasTracked.current && opportunityId && pageState !== PAGE_STATES.ALREADY_COMPLETED) {
      hasTracked.current = true;
      fireRenewalJourneyActivity(opportunityId, RenewalJourneyEvent.SUBMITTED, {location: RenewalJourneyLocation.COMPLETION});
    }
  }, [opportunityId, pageState]);

  const steps = [
    m('renewalClick.completion.nextStep1'),
    m('renewalClick.completion.nextStep2'),
    m('renewalClick.completion.nextStep3'),
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.iconWrapper}>
        <CheckCircleIcon sx={{fontSize: 44, color: 'success.main'}} />
      </Box>

      <Typography sx={styles.title}>{m('renewalClick.completion.title')}</Typography>
      <Typography sx={styles.subtitle}>{m('renewalClick.completion.subtitle')}</Typography>

      {/* Summary card */}
      <Box sx={styles.card}>
        <Box sx={styles.row}>
          <Typography sx={styles.rowLabel}>{m('renewalClick.completion.vehicleLabel')}</Typography>
          <Typography sx={styles.rowValue}>{vehicle.licensePlate}</Typography>
        </Box>
        <Divider />
        <Box sx={styles.row}>
          <Typography sx={styles.rowLabel}>{m('renewalClick.completion.totalLabel')}</Typography>
          <Typography sx={[styles.rowValue, styles.priceValue]}>
            {formatCurrency(pricing.totalPremium)}
          </Typography>
        </Box>
        <Divider />
        <Box sx={styles.row}>
          <Typography sx={styles.rowLabel}>{m('renewalClick.completion.emailLabel')}</Typography>
          <Typography sx={styles.emailValue}>
            {m('renewalClick.completion.emailValue')}
          </Typography>
        </Box>
      </Box>

      {/* Status message */}
      <Box sx={styles.statusRow}>
        <Typography sx={styles.statusText}>{m('renewalClick.completion.statusMessage')}</Typography>
      </Box>

      {/* What happens next */}
      <Box
        sx={[
          styles.nextSection,
          {bgcolor: 'background.default', mt: 3},
        ]}
      >
        <Typography sx={styles.nextTitle}>{m('renewalClick.completion.nextTitle')}</Typography>
        {steps.map((text, i) => (
          <Box key={i} sx={styles.nextItem}>
            <CheckCircleIcon sx={{fontSize: 18, color: 'success.main', mt: 0.25, flexShrink: 0}} />
            <Typography sx={styles.nextItemText}>{text}</Typography>
          </Box>
        ))}
      </Box>

      {/* Help card */}
      <Box sx={{mb: 2.5}}>
        <RenewalHelpCard
          title={m('renewalClick.completion.helpTitle')}
          description={m('renewalClick.completion.helpDescription')}
        />
      </Box>
    </Box>
  );
};

export default CompletionScreen;
